import { fmt } from "./cards";
import { SKILL_LABEL } from "./engine";

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const P = (x) => `${Math.round(x * 100)}%`;

function reviewDecision(d) {
  const tag = `Hand #${d.hand} (${d.hole}${d.board ? ` on ${d.board}` : ""})`;
  if (d.street === "preflop") {
    if (d.type === "fold" && d.chen >= 12 && d.raiseCount <= 1 && d.toCall > 0)
      return { kind: "leak", weight: 3, text: `${tag}: folded a premium hand pre-flop facing only ${d.raiseCount ? "one raise" : "the blinds"}.` };
    if (d.effBB <= 12 && d.raiseCount === 0 && (d.type === "call" || (d.type === "raise" && d.amount < d.maxTo * 0.5)) && d.pos !== "BB")
      return { kind: "leak", weight: 2, text: `${tag}: with ${d.effBB.toFixed(1)} BB you ${d.type === "call" ? "limped" : "made a small raise"}. Under ~12 BB, shove-or-fold keeps fold equity.` };
    if ((d.type === "call" || d.type === "raise") && d.raiseCount === 0 && d.chen < 6 && (d.pos === "EP" || d.pos === "MP"))
      return { kind: "leak", weight: 1.5, text: `${tag}: played a weak hand from early position (${d.pos}). Tighten up when many players act after you.` };
    if (d.type === "call" && d.raiseCount === 0 && !["SB", "BB"].includes(d.pos) && d.effBB > 12)
      return { kind: "leak", weight: 1, text: `${tag}: open-limped from ${d.pos}. Raising or folding wins more pots uncontested.` };
    if (d.type === "raise" && d.raiseCount >= 1 && d.chen >= 12)
      return { kind: "good", weight: 2, text: `${tag}: re-raised a premium hand pre-flop to build the pot.` };
    if (d.type === "raise" && d.raiseCount === 0 && ["BTN", "CO"].includes(d.pos))
      return { kind: "good", weight: 0.5, text: `${tag}: opened from late position (${d.pos}) — good positional aggression.` };
    return null;
  }
  const eq = d.equity, odds = d.potOdds;
  if (d.type === "call" && eq < odds - 0.08)
    return { kind: "leak", weight: 2 + (odds - eq) * 5, text: `${tag}: called ${fmt(d.toCall)} needing ${P(odds)} equity but had only ~${P(eq)}.` };
  if (d.type === "fold" && eq > odds + 0.15 && eq > 0.35)
    return { kind: "leak", weight: 2 + (eq - odds) * 5, text: `${tag}: folded with ~${P(eq)} equity when you only needed ${P(odds)}.` };
  if (d.type === "check" && d.toCall === 0 && eq > 0.8 && d.street === "river")
    return { kind: "leak", weight: 1.5, text: `${tag}: checked the river with a very strong hand (~${P(eq)}). Bet for value.` };
  if (d.type === "raise" && eq > 0.7)
    return { kind: "good", weight: 1.5, text: `${tag}: bet/raised for value with ~${P(eq)} equity.` };
  if (d.type === "fold" && eq < odds - 0.05 && d.toCall > 0)
    return { kind: "good", weight: 1, text: `${tag}: disciplined fold — ~${P(eq)} equity vs ${P(odds)} needed.` };
  if (d.type === "call" && eq >= odds)
    return { kind: "good", weight: 0.5, text: `${tag}: profitable call — ~${P(eq)} equity vs ${P(odds)} needed.` };
  return null;
}

function styleTips(vpip, pfr, af, hands) {
  const tips = [];
  if (hands < 8) return ["Play more hands per tournament to get a more reliable read on your style."];
  if (vpip > 35) tips.push(`Your VPIP of ${vpip}% is loose. Aim for roughly 20–30% at a short-handed table and fold more marginal hands.`);
  else if (vpip < 15) tips.push(`Your VPIP of ${vpip}% is very tight. Open up a bit in late position and from the blinds.`);
  else tips.push(`Your VPIP of ${vpip}% is in a solid range.`);
  if (vpip - pfr > 12) tips.push(`Big gap between VPIP (${vpip}%) and PFR (${pfr}%) — you call pre-flop too often. Prefer raising or folding.`);
  if (af < 1) tips.push(`Aggression factor ${af.toFixed(1)} is passive. Betting and raising wins pots without showdown.`);
  else if (af > 4) tips.push(`Aggression factor ${af.toFixed(1)} is very high. Make sure your bluffs are chosen with good blockers or draws.`);
  else tips.push(`Aggression factor ${af.toFixed(1)} is balanced.`);
  return tips;
}

export function buildSummary(s, decisions) {
  const me = s.players[0];
  const st = s.stats[0];
  const reviews = decisions.map(reviewDecision).filter(Boolean);
  const leaks = reviews.filter((r) => r.kind === "leak").sort((a, b) => b.weight - a.weight);
  const goods = reviews.filter((r) => r.kind === "good").sort((a, b) => b.weight - a.weight);
  const evaluated = decisions.filter((d) => d.street !== "preflop").length;
  const postGood = reviews.filter((r) => r.kind === "good" && !r.text.includes("pre-flop") && !r.text.includes("opened")).length;
  const postLeak = reviews.filter((r) => r.kind === "leak" && (r.text.includes("equity") || r.text.includes("river"))).length;
  const vpip = pct(st.vpip, st.dealt), pfr = pct(st.pfr, st.dealt);
  const af = st.calls ? st.aggr / st.calls : st.aggr;
  return {
    place: me.place || 1,
    total: s.players.length,
    hands: s.handNum,
    dealt: st.dealt,
    vpip, pfr, af,
    handsWon: st.won,
    winRate: pct(st.won, st.dealt),
    showdowns: st.showdowns,
    sdWon: st.sdWon,
    biggestPot: st.biggestPot,
    bustInfo: s.bustInfo,
    decisionsCount: decisions.length,
    postflopAccuracy: evaluated ? pct(postGood, postGood + postLeak || 1) : null,
    leaks: leaks.slice(0, 6),
    goods: goods.slice(0, 5),
    tips: styleTips(vpip, pfr, af, st.dealt),
    chipHistory: s.chipHistory,
    finalLevel: s.level + 1,
    finalBlinds: `${fmt(s.sb)}/${fmt(s.bb)}`,
    field: s.players.slice(1).map((p) => {
      const x = s.stats[p.id];
      return { name: p.name, skill: SKILL_LABEL[p.skill], place: p.place, stack: p.out ? 0 : p.stack, vpip: pct(x.vpip, x.dealt), pfr: pct(x.pfr, x.dealt), won: x.won };
    }),
  };
}
